import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { StatusBadge, LoadingSpinner, Modal } from '../../components/UI';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://cmsapi3.vdavns.com/api').replace('/api', '');

// Document status badge
const DocStatusBadge = ({ status }) => {
  const map = {
    uploaded:        { label: 'Uploaded',        cls: 'bg-blue-100 text-blue-700' },
    pending_review:  { label: 'Pending Review',  cls: 'bg-yellow-100 text-yellow-700' },
    verified:        { label: 'Verified ✓',      cls: 'bg-green-100 text-green-700' },
    objection_raised:{ label: 'Objection Raised',cls: 'bg-orange-100 text-orange-700' },
    approved:        { label: 'Approved',         cls: 'bg-green-100 text-green-700' },
    rejected:        { label: 'Rejected',         cls: 'bg-red-100 text-red-700' },
  };
  const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>;
};

export default function ApplicationStatus() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Resubmit state
  const [resubmitModal, setResubmitModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [docRows, setDocRows] = useState([{ name: '', file: null }]);
  const [submitting, setSubmitting] = useState(false);

  // Pull state
  const [pullModal, setPullModal] = useState(false);
  const [pullReason, setPullReason] = useState('');

  const load = () => {
    setLoading(true);
    api.get(`/applications/${id}`).then(r => {
      setData(r);
      if (r.application) {
        const a = r.application;
        setEditForm({
          applicant_name:    a.applicant_name    || '',
          applicant_address: a.applicant_address || '',
          applicant_email:   a.applicant_email   || '',
          applicant_mobile:  a.applicant_mobile  || '',
          latitude:          a.latitude          || '',
          longitude:         a.longitude         || '',
          description:       a.description       || '',
        });
      }
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const ef = (k, v) => setEditForm(p => ({ ...p, [k]: v }));

  const addDocRow = () => setDocRows(r => [...r, { name: '', file: null }]);
  const removeDocRow = (i) => setDocRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i, k, v) => setDocRows(r => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));

  const getLocation = () => {
    navigator.geolocation.getCurrentPosition(
      pos => { ef('latitude', pos.coords.latitude.toFixed(6)); ef('longitude', pos.coords.longitude.toFixed(6)); },
      () => toast.error('Location access denied')
    );
  };

  const handleResubmit = async () => {
    if (!editForm.applicant_name || !editForm.applicant_mobile)
      return toast.error('Name and Mobile are required');
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(editForm).forEach(([k, v]) => fd.append(k, v));
      docRows.forEach((row, i) => {
        if (row.file) {
          fd.append('files', row.file);
          fd.append(`doc_name_${i}`, row.name || row.file.name);
        }
      });
      await api.post(`/applications/${id}/resubmit`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Application resubmitted!');
      setResubmitModal(false);
      load();
    } catch { toast.error('Resubmit failed'); }
    finally { setSubmitting(false); }
  };

  const handlePull = async () => {
    try {
      await api.post(`/applications/${id}/pull`, { reason: pullReason });
      toast.success('Application pulled');
      navigate('/user/applications');
    } catch { toast.error('Failed to pull'); }
  };

  if (loading) return <LoadingSpinner size="lg" className="py-20" />;
  if (!data) return <div className="text-center py-20 text-gray-500">Not found</div>;

  const { application: app, documents, workflow, routing = [] } = data;
  const canResubmit = ['objection_by_related', 'objection_by_final'].includes(app.status);
  const canPull = !['approved_final', 'rejected_final', 'pulled'].includes(app.status);
  const isApproved = app.status === 'approved_final';

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      {/* ── Header ── */}
      <div className="card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <h1 className="text-xl font-bold text-gray-800">{app.noc_name}</h1>
              <StatusBadge status={app.status} />
            </div>
            <p className="text-sm text-gray-500 font-mono">{app.application_no}</p>
            <p className="text-xs text-gray-400 mt-1">
              Submitted: {app.submitted_at ? format(new Date(app.submitted_at), 'dd MMM yyyy, hh:mm a') : '—'}
              {app.resubmit_count > 0 && <span className="ml-2 text-orange-500">Resubmitted {app.resubmit_count}x</span>}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Download button — blob fetch to force PDF, never HTML */}
            {isApproved && app.final_noc_pdf &&
              app.final_noc_pdf.split('.').pop().toLowerCase() === 'pdf' && (
              <button
                onClick={async () => {
                  const fileUrl = `${API_BASE}${app.final_noc_pdf}`;
                  const fileName = `NOC-${app.application_no}.pdf`;
                  try {
                    toast.loading('Downloading...', { id: 'dl' });
                    const token = localStorage.getItem('noc_token');
                    const response = await fetch(fileUrl, {
                      headers: token ? { Authorization: `Bearer ${token}` } : {},
                    });
                    if (!response.ok) throw new Error(`${response.status}`);
                    const blob = await response.blob();
                    if (blob.size === 0) throw new Error('Empty file');
                    const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(blobUrl);
                    toast.dismiss('dl');
                    toast.success('Downloaded!');
                  } catch (err) {
                    toast.dismiss('dl');
                    // Fallback: open in new tab
                    window.open(fileUrl, '_blank');
                    toast.success('Opening in new tab. Use Ctrl+S to save.');
                  }
                }}
                className="btn-success text-sm flex items-center gap-2"
              >
                ⬇️ Download NOC PDF
              </button>
            )}
            {isApproved && (!app.final_noc_pdf || app.final_noc_pdf.split('.').pop().toLowerCase() !== 'pdf') && (
              <span className="text-sm bg-yellow-50 border border-yellow-200 text-yellow-700 px-3 py-2 rounded-lg">
                ⏳ NOC PDF being prepared by department...
              </span>
            )}
            {canResubmit && (
              <button onClick={() => setResubmitModal(true)} className="btn-warning text-sm">
                ↩ Edit &amp; Resubmit
              </button>
            )}
            {canPull && (
              <button onClick={() => setPullModal(true)} className="btn-danger text-sm">
                🚫 Pull Application
              </button>
            )}
          </div>
        </div>

        {canResubmit && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl">
            <p className="text-sm font-semibold text-orange-800">⚠️ Action Required — Objection</p>
            <p className="text-sm text-orange-700 mt-1">
              {app.last_objection_remark || 'Your application has an objection. Please edit and resubmit.'}
            </p>
          </div>
        )}
        {isApproved && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-sm font-semibold text-green-800">🎉 Congratulations! NOC Approved</p>
            <p className="text-sm text-green-700 mt-1">Your NOC has been approved. Download the certificate above.</p>
          </div>
        )}
      </div>

      {/* ── Routing flow ── */}
      {routing.length > 0 && (
        <div className="card">
          <h2 className="font-semibold text-gray-800 mb-3">Approval Flow</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex flex-col items-center">
              <div className="w-9 h-9 bg-blue-100 border-2 border-blue-400 rounded-full flex items-center justify-center text-sm">👤</div>
              <p className="text-xs mt-1 text-gray-500">Citizen</p>
            </div>
            {routing.map((step, i) => (
              <React.Fragment key={step.id || i}>
                <div className="text-gray-300 text-xl">→</div>
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-sm
                    ${app.current_dept_id === step.dept_id
                      ? 'bg-yellow-100 border-yellow-500'
                      : step.is_final_approval
                        ? 'bg-purple-100 border-purple-400'
                        : 'bg-green-100 border-green-400'}`}>
                    {step.is_final_approval ? '🏛️' : '🏢'}
                  </div>
                  <p className="text-xs mt-1 text-gray-600 text-center max-w-[80px] leading-tight">
                    {step.dept_name}
                    {app.current_dept_id === step.dept_id && <span className="block text-yellow-600 font-bold">← Here</span>}
                  </p>
                </div>
              </React.Fragment>
            ))}
            <div className="text-gray-300 text-xl">→</div>
            <div className="flex flex-col items-center">
              <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-sm
                ${isApproved ? 'bg-green-200 border-green-600' : 'bg-gray-100 border-gray-300'}`}>
                ✅
              </div>
              <p className="text-xs mt-1 text-gray-500">Issued</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Application Details ── */}
        <div className="card">
          <h2 className="font-semibold text-gray-800 mb-4">Application Details</h2>
          <div className="space-y-2">
            {[
              ['Applicant', app.applicant_name],
              ['Mobile',    app.applicant_mobile],
              ['Email',     app.applicant_email],
              ['Address',   app.applicant_address],
              ['Description', app.description],
            ].map(([label, value]) => value && (
              <div key={label} className="flex gap-3 py-1.5 border-b border-gray-50 last:border-0">
                <span className="text-xs text-gray-400 w-24 flex-shrink-0">{label}</span>
                <span className="text-sm text-gray-700">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Documents with status (Fix #4) ── */}
        <div className="card">
          <h2 className="font-semibold text-gray-800 mb-4">Documents ({documents.length})</h2>
          {documents.length === 0
            ? <p className="text-sm text-gray-400 text-center py-4">No documents</p>
            : (
              <div className="space-y-2">
                {documents.map(doc => (
                  <div key={doc.id} className="p-3 border border-gray-100 rounded-xl">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-lg flex-shrink-0">{doc.file_type?.includes('pdf') ? '📄' : '🖼️'}</span>
                        <p className="text-xs font-medium text-gray-800 truncate">{doc.document_name}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <DocStatusBadge status={doc.doc_status} />
                        <button onClick={() => window.open(`${API_BASE}${doc.file_path}`, '_blank', 'noopener,noreferrer')} className="text-blue-500 hover:text-blue-700 text-xs underline">↗ View</button>
                      </div>
                    </div>
                    {doc.status_remark && (
                      <p className="text-xs text-orange-600 mt-1 pl-7">Note: {doc.status_remark}</p>
                    )}
                    {doc.reviewer_name && (
                      <p className="text-xs text-gray-400 pl-7">Reviewed by: {doc.reviewer_name}</p>
                    )}
                  </div>
                ))}
              </div>
            )
          }
        </div>
      </div>

      {/* ── Timeline ── */}
      <div className="card">
        <h2 className="font-semibold text-gray-800 mb-6">Application Timeline</h2>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
          <div className="space-y-5">
            {workflow.map(w => {
              const icons = { submitted:'📝', forwarded:'➡️', approved:'✅', rejected:'❌', objection:'⚠️', resubmitted:'🔄', final_approved:'🏆', final_rejected:'❌', pulled:'🚫' };
              return (
                <div key={w.id} className="flex items-start gap-4 relative">
                  <div className="relative z-10 w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center text-sm flex-shrink-0">
                    {icons[w.action] || '•'}
                  </div>
                  <div className="flex-1 pb-1">
                    <div className="flex justify-between flex-wrap gap-1">
                      <p className="font-medium text-sm text-gray-800 capitalize">{w.action.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-gray-400">{format(new Date(w.created_at), 'dd MMM yyyy, hh:mm a')}</p>
                    </div>
                    {w.actor_name && <p className="text-xs text-gray-500">By: {w.actor_name}</p>}
                    {(w.from_dept || w.to_dept) && (
                      <p className="text-xs text-gray-500">
                        {w.from_dept && `From: ${w.from_dept}`}
                        {w.from_dept && w.to_dept && ' → '}
                        {w.to_dept && `To: ${w.to_dept}`}
                      </p>
                    )}
                    {w.remarks && (
                      <div className="mt-1 p-2 bg-gray-50 rounded-lg border border-gray-100">
                        <p className="text-xs text-gray-600">"{w.remarks}"</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Resubmit Modal (Fix #2 + #3) ── */}
      <Modal isOpen={resubmitModal} onClose={() => setResubmitModal(false)} title="Edit & Resubmit Application" size="xl">
        <div className="space-y-5">
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl text-sm text-orange-700">
            ⚠️ <strong>Objection:</strong> {app.last_objection_remark || 'Please correct and resubmit'}
          </div>

          {/* Editable form fields */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">Edit Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Full Name <span className="text-red-500">*</span></label>
                <input className="form-input" value={editForm.applicant_name || ''}
                  onChange={e => ef('applicant_name', e.target.value)} />
              </div>
              <div>
                <label className="form-label">Mobile <span className="text-red-500">*</span></label>
                <input className="form-input" value={editForm.applicant_mobile || ''}
                  onChange={e => ef('applicant_mobile', e.target.value)} />
              </div>
              <div>
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={editForm.applicant_email || ''}
                  onChange={e => ef('applicant_email', e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="form-label">Address</label>
                <textarea className="form-input" rows={2} value={editForm.applicant_address || ''}
                  onChange={e => ef('applicant_address', e.target.value)} />
              </div>
              <div>
                <label className="form-label">Latitude</label>
                <input className="form-input" value={editForm.latitude || ''}
                  onChange={e => ef('latitude', e.target.value)} />
              </div>
              <div>
                <label className="form-label">Longitude</label>
                <input className="form-input" value={editForm.longitude || ''}
                  onChange={e => ef('longitude', e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <button type="button" onClick={getLocation} className="btn-secondary text-xs">
                  📍 Get Current Location
                </button>
              </div>
              <div className="sm:col-span-2">
                <label className="form-label">Description</label>
                <textarea className="form-input" rows={3} value={editForm.description || ''}
                  onChange={e => ef('description', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Previous documents */}
          {documents.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Previous Documents</h3>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {documents.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs">
                    <span className="font-medium text-gray-700 truncate max-w-[60%]">{doc.document_name}</span>
                    <div className="flex items-center gap-2">
                      <DocStatusBadge status={doc.doc_status} />
                      <button onClick={() => window.open(`${API_BASE}${doc.file_path}`, '_blank', 'noopener,noreferrer')} className="text-blue-500 hover:underline text-xs">View</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New documents upload (Fix #3) */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">Add / Replace Documents</h3>
            <div className="space-y-2">
              {docRows.map((row, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input className="form-input flex-1 text-sm" placeholder="Document name"
                    value={row.name} onChange={e => updateRow(i, 'name', e.target.value)} />
                  <label className={`flex-1 cursor-pointer border rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${row.file ? 'border-green-400 bg-green-50 text-green-700' : 'border-gray-300 text-gray-500'}`}>
                    {row.file ? `✓ ${row.file.name.slice(0, 18)}...` : '📎 Choose file'}
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={e => updateRow(i, 'file', e.target.files[0])} />
                  </label>
                  {i > 0 && (
                    <button type="button" onClick={() => removeDocRow(i)}
                      className="text-red-400 hover:text-red-600 text-lg leading-none px-1">✕</button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addDocRow}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
              ➕ Add More Document
            </button>
          </div>

          <div className="flex gap-3 justify-end pt-2 border-t">
            <button className="btn-secondary" onClick={() => setResubmitModal(false)}>Cancel</button>
            <button className="btn-primary" onClick={handleResubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : '↩ Resubmit Application'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Pull Modal ── */}
      <Modal isOpen={pullModal} onClose={() => setPullModal(false)} title="Pull Application" size="sm">
        <p className="text-sm text-gray-600 mb-3">Are you sure you want to withdraw this application?</p>
        <textarea className="form-input" rows={3} placeholder="Reason for withdrawal..."
          value={pullReason} onChange={e => setPullReason(e.target.value)} />
        <div className="flex gap-3 justify-end mt-4">
          <button className="btn-secondary" onClick={() => setPullModal(false)}>Cancel</button>
          <button className="btn-danger" onClick={handlePull}>Pull Application</button>
        </div>
      </Modal>
    </div>
  );
}